import React, { useEffect, useRef } from "react";
import { FormGroup, Label, Input, Row, Col } from "reactstrap";
import { LoadScript, Autocomplete } from "@react-google-maps/api";
import Select from "react-select";
import styles from "./MerchantForm.module.scss";

const libraries = ["places"];

const MerchantForm = ({
  selectedTypes,
  formData,
  setFormData,
  errors = {},
  categoriesByType = {},
  subCategoriesByType = {},
  genderOptions = [],
  genderOptionsForType,
  resolveGenderSelection,
  onLoadSubCategories,
  onSubCategoryChange,
}) => {
  const errorBorderStyle = { borderColor: "#d9534f" };
  const autocompleteRefs = useRef({});

  // Every non-marketplace type renders a detail form.
  const validTypes = selectedTypes.filter((type) => type !== "marketplace");

  const handleChange = (e, type) => {
    const { name, value } = e.target;
    
    // Handle category change: clear subcategory and call callback OUTSIDE setState
    if (name === "category") {
      setFormData((prevData) => ({
        ...prevData,
        [`${type}Detail`]: {
          ...prevData[`${type}Detail`],
          category: value,
          subCategory: "", // Clear subcategory when category changes
        },
      }));
      // Call after state update
      if (typeof onLoadSubCategories === "function") {
        onLoadSubCategories(value, type);
      }
      return;
    }
    
    // Handle subcategory change
    if (name === "subCategory") {
      setFormData((prevData) => ({
        ...prevData,
        [`${type}Detail`]: {
          ...prevData[`${type}Detail`],
          subCategory: value,
        },
      }));
      // Gym/academy gender is driven by the sub-category
      if (typeof onSubCategoryChange === "function") {
        onSubCategoryChange(value, type);
      }
      return;
    }
    
    // Handle all other changes
    setFormData((prevData) => ({
      ...prevData,
      [`${type}Detail`]: {
        ...prevData[`${type}Detail`],
        [name]: value,
      },
    }));
  };

  const handleLocationChange = (e, type) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [`${type}Detail`]: {
        ...prevData[`${type}Detail`],
        location: {
          ...prevData[`${type}Detail`]?.location,
          [name]: value,
        },
      },
    }));
  };

  const handlePlaceChanged = (type) => {
    const autocomplete = autocompleteRefs.current[type];
    const place = autocomplete.getPlace();
    if (place && place.geometry) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const address = place.formatted_address || place.name;

      setFormData((prevData) => ({
        ...prevData,
        [`${type}Detail`]: {
          ...prevData[`${type}Detail`],
          location: {
            address,
            latitude: lat,
            longitude: lng,
          },
        },
      }));
    }
  };

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      type: selectedTypes,
    }));
  }, [selectedTypes]);

  useEffect(() => {
  }, [formData]);

  if (validTypes.length === 0) return null;

  return (
    <LoadScript
      googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
      libraries={libraries}
    >
      <div className={styles.merchantFormContainer} style={{ marginTop: "10px" }}>
        {validTypes.map((type) => (
          <div key={type} className={styles.typeSection}>
            <h5 className={styles.sectionTitle}>
              {type.charAt(0).toUpperCase() + type.slice(1)} Details
            </h5>
            <Row>
              <Col lg={6} md={6} sm={12}>
                <FormGroup className={styles.formGroup} data-error-field={`${type}Name`}>
                  <Label>Name</Label>
                  <Input
                    type="text"
                    name="name"
                    value={formData[`${type}Detail`]?.name || ""}
                    onChange={(e) => handleChange(e, type)}
                    className={styles.inputField}
                    style={errors[`${type}Name`] ? errorBorderStyle : undefined}
                  />
                  {errors[`${type}Name`] && (
                    <div className="text-danger small mt-1">{errors[`${type}Name`]}</div>
                  )}
                </FormGroup>
              </Col>
              <Col lg={6} md={6} sm={12}>
                <FormGroup className={styles.formGroup} data-error-field={`${type}Description`}>
                  <Label>Description</Label>
                  <Input
                    style={{ height: "46px", ...(errors[`${type}Description`] ? errorBorderStyle : {}) }}
                    type="textarea"
                    name="description"
                    value={formData[`${type}Detail`]?.description || ""}
                    onChange={(e) => handleChange(e, type)}
                    className={styles.inputField}
                  />
                  {errors[`${type}Description`] && (
                    <div className="text-danger small mt-1">{errors[`${type}Description`]}</div>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <Row>
              {/* Any non-marketplace type that has categories shows the
                  category + sub-category dropdowns. */}
              {(categoriesByType[type] || []).length > 0 && (
                <Col lg={3} md={6} sm={12}>
                  <FormGroup className={styles.formGroup} data-error-field={`${type}Category`}>
                    <Label>Category</Label>
                    <Input
                      type="select"
                      name="category"
                      value={formData[`${type}Detail`]?.category || ""}
                      onChange={(e) => handleChange(e, type)}
                      className={styles.inputField}
                      style={errors[`${type}Category`] ? errorBorderStyle : undefined}
                    >
                      <option value="">Select Category</option>
                      {(categoriesByType[type] || []).map((cat) => (
                        <option key={cat._id} value={cat._id}>
                          {cat.name}
                        </option>
                      ))}
                    </Input>
                    {errors[`${type}Category`] && (
                      <div className="text-danger small mt-1">{errors[`${type}Category`]}</div>
                    )}
                  </FormGroup>
                </Col>
              )}

              {(categoriesByType[type] || []).length > 0 && (
                <Col lg={3} md={6} sm={12}>
                  <FormGroup className={styles.formGroup} data-error-field={`${type}SubCategory`}>
                    <Label>Sub-Category</Label>
                    <Input
                      type="select"
                      name="subCategory"
                      value={formData[`${type}Detail`]?.subCategory || ""}
                      onChange={(e) => handleChange(e, type)}
                      className={styles.inputField}
                      style={errors[`${type}SubCategory`] ? errorBorderStyle : undefined}
                      disabled={!formData[`${type}Detail`]?.category}
                    >
                      <option value="">
                        {formData[`${type}Detail`]?.category
                          ? "Select Sub-Category"
                          : "Select category first"}
                      </option>
                      {(subCategoriesByType[type] || []).map((sub) => (
                        <option key={sub._id} value={sub._id}>
                          {sub.name}
                        </option>
                      ))}
                    </Input>
                    {errors[`${type}SubCategory`] && (
                      <div className="text-danger small mt-1">{errors[`${type}SubCategory`]}</div>
                    )}
                  </FormGroup>
                </Col>
              )}

              {/* Gender Type for Gym/Academy - Shows after sub-category selection */}
              {(categoriesByType[type] || []).length > 0 && (type === "gym" || type === "academy") && (
                <Col lg={3} md={6} sm={12}>
                  <FormGroup className={styles.formGroup} data-error-field={`${type}GenderType`}>
                    <Label>Gender Type</Label>
                    <Select
                      isMulti
                      isDisabled={!formData[`${type}Detail`]?.subCategory}
                      placeholder={
                        formData[`${type}Detail`]?.subCategory
                          ? "Select..."
                          : "Select sub-category first"
                      }
                      options={genderOptionsForType ? genderOptionsForType(type) : genderOptions}
                      value={(formData[`${type}Detail`]?.genderType || []).map((g) =>
                        genderOptions.find((opt) => opt.value === g)
                      )}
                      onChange={(selected) =>
                        setFormData((prev) => ({
                          ...prev,
                          [`${type}Detail`]: {
                            ...prev[`${type}Detail`],
                            genderType: resolveGenderSelection
                              ? resolveGenderSelection(selected, prev[`${type}Detail`]?.genderType || [])
                              : selected.map(s => s.value),
                          },
                        }))
                      }
                      styles={
                        errors[`${type}GenderType`]
                          ? { control: (base) => ({ ...base, borderColor: "#d9534f" }) }
                          : undefined
                      }
                    />
                    {errors[`${type}GenderType`] && (
                      <div className="text-danger small mt-1">{errors[`${type}GenderType`]}</div>
                    )}
                  </FormGroup>
                </Col>
              )}

              <Col lg={(categoriesByType[type] || []).length > 0 ? 3 : 4} md={6} sm={12}>
                <FormGroup className={styles.formGroup} data-error-field={`${type}Location`}>
                  <Label>Address</Label>
                  <Autocomplete
                    onLoad={(autocomplete) => {
                      autocompleteRefs.current[type] = autocomplete;
                    }}
                    onPlaceChanged={() => handlePlaceChanged(type)}
                  >
                    <Input
                      type="text"
                      name="address"
                      placeholder="Search address..."
                      value={formData[`${type}Detail`]?.location?.address || ""}
                      onChange={(e) => handleLocationChange(e, type)}
                      className={styles.inputField}
                      style={errors[`${type}Location`] ? errorBorderStyle : undefined}
                    />
                  </Autocomplete>
                  {errors[`${type}Location`] && (
                    <div className="text-danger small mt-1">{errors[`${type}Location`]}</div>
                  )}
                </FormGroup>
              </Col>
            </Row>

            {/* Second row for Latitude and Longitude */}
            <Row>
              <Col lg={6} md={6} sm={12}>
                <FormGroup className={styles.formGroup}>
                  <Label>Latitude</Label>
                  <Input
                    type="number"
                    name="latitude"
                    value={formData[`${type}Detail`]?.location?.latitude || ""}
                    readOnly
                    className={styles.inputField}
                  />
                </FormGroup>
              </Col>

              <Col lg={6} md={6} sm={12}>
                <FormGroup className={styles.formGroup}>
                  <Label>Longitude</Label>
                  <Input
                    type="number"
                    name="longitude"
                    value={formData[`${type}Detail`]?.location?.longitude || ""}
                    readOnly
                    className={styles.inputField}
                  />
                </FormGroup>
              </Col>
            </Row>
          </div>
        ))}
      </div>
    </LoadScript>
  );
};

export default MerchantForm;
